import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { ChevronDown, ChevronRight, ChevronUp, GripVertical, Move, Plus, X } from "lucide-react";

import { InsigniaDeIa } from "@/components/plantillas/GeneracionConIa";
import {
  ChipDeTipo,
  ConteoDeObligatoriedad,
  PildoraDeObligatoriedad,
} from "@/components/plantillas/etiquetas";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ItemDeBorrador } from "@/lib/borradorDePlantilla";
import type {
  DestinoDeSoltado,
  NodoDeGrupo,
  NodoDeSubgrupo,
  OrigenDeArrastre,
} from "@/lib/estructuraDePlantilla";
import { cn } from "@/lib/utils";

type TipoDeDestino = "I" | "S" | "G" | "E";

/* Qué se puede soltar sobre qué: un ítem cae en cualquier fila; un subgrupo en
   otro subgrupo o en un grupo; un grupo solo en otro grupo. */
const ACEPTA: Record<OrigenDeArrastre["tipo"], TipoDeDestino[]> = {
  item: ["I", "S", "G", "E"],
  subgrupo: ["S", "G"],
  grupo: ["G"],
};

export interface AccionesDelArbol {
  seleccionar: (ref: string) => void;
  ciclar: (ref: string) => void;
  moverItem: (ref: string, dir: -1 | 1) => void;
  pedirMoverItem: (ref: string) => void;
  pedirQuitar: (ref: string) => void;
  nuevoItem: (grupo: string, subgrupo: string | null) => void;
  nuevoSubgrupo: (grupo: string) => void;
  moverGrupo: (grupo: string, dir: -1 | 1) => void;
  moverSubgrupo: (grupo: string, subgrupo: string | null, dir: -1 | 1) => void;
  pedirMoverSubgrupo: (grupo: string, subgrupo: string | null) => void;
  eliminarVacio: (grupo: string, subgrupo: string | null) => void;
  alternarGrupo: (grupo: string) => void;
  alternarSubgrupo: (grupo: string, subgrupo: string | null) => void;
  soltar: (origen: OrigenDeArrastre, destino: DestinoDeSoltado) => void;
}

interface ArbolProps {
  arbol: NodoDeGrupo[];
  items: Record<string, ItemDeBorrador>;
  grupoEnElMaestro: (grupo: string) => boolean;
  subgrupoEnElMaestro: (grupo: string, subgrupo: string) => boolean;
  /** Hay búsqueda o filtro: no se reordena ni se colapsa. */
  filtrando: boolean;
  grupoColapsado: (grupo: string) => boolean;
  subgrupoColapsado: (grupo: string, subgrupo: string | null) => boolean;
  seleccionado: string | null;
  acciones: AccionesDelArbol;
}

interface PropsDeArrastre {
  draggable: boolean;
  className: string;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

const claveDeSubgrupo = (g: string, s: string | null) => `S:${g}|${s ?? ""}`;

function BotonDeFila({
  etiqueta,
  onClick,
  children,
  destructivo,
}: {
  etiqueta: string;
  onClick: () => void;
  children: ReactNode;
  destructivo?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={etiqueta}
      title={etiqueta}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "text-ink-muted hover:text-ink size-[1.625rem] rounded-[0.4375rem] [&_svg]:size-3.5",
        destructivo && "hover:bg-destructive/10 hover:text-destructive",
      )}
    >
      {children}
    </Button>
  );
}

/** "+ Ítem" / "+ Subgrupo": chip relleno que acompaña al título de su bloque. */
function ChipDeAgregar({
  onClick,
  blanco,
  children,
}: {
  onClick: () => void;
  /** Sobre la barra gris de un subgrupo va blanco. */
  blanco?: boolean;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "text-ink-body hover:text-ink h-auto gap-1 rounded-lg px-2.5 py-1 text-[0.71875rem]",
        blanco ? "bg-background hover:bg-background" : "bg-muted/60 hover:bg-muted",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Plus className="size-3" />
      {children}
    </Button>
  );
}

/**
 * El árbol Grupo › Subgrupo › Ítem de una plantilla.
 *
 * Es un árbol y no una `Table`: sus filas NO son equivalentes (un grupo, un
 * subgrupo y un ítem tienen forma y acciones distintas), que es justo el caso
 * al que apunta la excepción de la regla "datos tabulares → Table".
 *
 * Se reordena de dos maneras que hacen lo mismo —arrastrando y con las
 * flechas—: el arrastre es rápido para distancias largas y las flechas son
 * la única forma con teclado.
 */
export function ArbolDeLaPlantilla({
  arbol,
  items,
  grupoEnElMaestro,
  subgrupoEnElMaestro,
  filtrando,
  grupoColapsado,
  subgrupoColapsado,
  seleccionado,
  acciones,
}: ArbolProps) {
  const arrastrando = useRef<{ clave: string; origen: OrigenDeArrastre } | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);

  function dnd(
    clave: string,
    tipo: TipoDeDestino,
    destino: DestinoDeSoltado,
    origen?: OrigenDeArrastre,
  ): PropsDeArrastre {
    const linea = sobre === clave && arrastrando.current;
    const antes =
      linea &&
      ((arrastrando.current?.origen.tipo === "item" && tipo === "I") ||
        (arrastrando.current?.origen.tipo === "subgrupo" && tipo === "S") ||
        (arrastrando.current?.origen.tipo === "grupo" && tipo === "G"));

    return {
      draggable: !filtrando && Boolean(origen),
      className: cn(
        linea && antes && "shadow-[inset_0_2px_0_0_var(--color-primary)]",
        linea && !antes && "shadow-[inset_0_0_0_2px_var(--color-primary)]",
      ),
      onDragStart: (e: DragEvent) => {
        if (!origen || filtrando) return;
        e.stopPropagation();
        e.dataTransfer.effectAllowed = "move";
        try {
          e.dataTransfer.setData("text/plain", clave);
        } catch {
          /* algunos navegadores no dejan escribir el dataTransfer */
        }
        arrastrando.current = { clave, origen };
      },
      onDragEnd: () => {
        arrastrando.current = null;
        setSobre(null);
      },
      onDragOver: (e: DragEvent) => {
        const actual = arrastrando.current;
        if (!actual || actual.clave === clave || !ACEPTA[actual.origen.tipo].includes(tipo)) return;
        e.preventDefault();
        e.stopPropagation();
        if (sobre !== clave) setSobre(clave);
      },
      onDrop: (e: DragEvent) => {
        const actual = arrastrando.current;
        if (!actual || !ACEPTA[actual.origen.tipo].includes(tipo)) return;
        e.preventDefault();
        e.stopPropagation();
        arrastrando.current = null;
        setSobre(null);
        acciones.soltar(actual.origen, destino);
      },
    };
  }

  const filas: ReactNode[] = [];

  arbol.forEach((nodo, iGrupo) => {
    if (filtrando && nodo.visibles === 0) return;

    const colapsado = !filtrando && grupoColapsado(nodo.grupo);
    const ultimo = nodo.subgrupos[nodo.subgrupos.length - 1];
    const ultimoSubgrupo = ultimo ? ultimo.subgrupo : null;
    const gk = `G:${nodo.grupo}`;
    const d = dnd(
      gk,
      "G",
      { tipo: "grupo", grupo: nodo.grupo, ultimoSubgrupo },
      { tipo: "grupo", grupo: nodo.grupo },
    );

    filas.push(
      <div
        key={gk}
        data-testid={`grupo-${nodo.grupo}`}
        draggable={d.draggable}
        onDragStart={d.onDragStart}
        onDragEnd={d.onDragEnd}
        onDragOver={d.onDragOver}
        onDrop={d.onDrop}
        title={filtrando ? undefined : "Arrastra para mover el grupo"}
        className={cn(
          "flex items-center gap-2.5 rounded-[0.625rem] px-3 pt-3.5 pb-2",
          iGrupo > 0 && "border-divisor mt-1 border-t",
          d.className,
        )}
      >
        <button
          type="button"
          aria-expanded={!colapsado}
          aria-label={`${colapsado ? "Expandir" : "Colapsar"} grupo ${nodo.grupo}`}
          onClick={() => acciones.alternarGrupo(nodo.grupo)}
          className="text-ink-muted flex size-6 shrink-0 items-center justify-center rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <ChevronRight className={cn("size-4 transition-transform", !colapsado && "rotate-90")} />
        </button>
        <span className="text-ink text-[1.0625rem] font-extrabold tracking-[-0.01em]">
          {nodo.grupo}
        </span>
        <span className="text-ink-ghost font-mono text-[0.6875rem] font-semibold">
          {filtrando ? `${nodo.visibles} de ${nodo.total}` : nodo.total}
        </span>
        {!grupoEnElMaestro(nodo.grupo) ? <Badge variant="info">Nuevo grupo</Badge> : null}
        <span className="ml-1.5 flex items-center gap-1">
          <ChipDeAgregar onClick={() => acciones.nuevoItem(nodo.grupo, ultimoSubgrupo)}>
            Ítem
            <span className="sr-only"> en {nodo.grupo}</span>
          </ChipDeAgregar>
          <ChipDeAgregar onClick={() => acciones.nuevoSubgrupo(nodo.grupo)}>
            Subgrupo
            <span className="sr-only"> en {nodo.grupo}</span>
          </ChipDeAgregar>
        </span>
        <span className="ml-auto flex items-center gap-2.5">
          <ConteoDeObligatoriedad {...nodo.conteo} />
          <span className="flex items-center gap-0.5">
            <BotonDeFila
              etiqueta={`Subir el grupo ${nodo.grupo}`}
              onClick={() => acciones.moverGrupo(nodo.grupo, -1)}
            >
              <ChevronUp />
            </BotonDeFila>
            <BotonDeFila
              etiqueta={`Bajar el grupo ${nodo.grupo}`}
              onClick={() => acciones.moverGrupo(nodo.grupo, 1)}
            >
              <ChevronDown />
            </BotonDeFila>
          </span>
        </span>
      </div>,
    );

    if (colapsado) return;

    if (nodo.subgrupos.length === 0) {
      const ek = `E:${nodo.grupo}|`;
      const e = dnd(ek, "E", { tipo: "subgrupo", grupo: nodo.grupo, subgrupo: null });
      filas.push(
        <FilaVacia
          key={ek}
          sangria="ml-6"
          mensaje="Grupo vacío. Arrastra ítems o subgrupos aquí, o crea un ítem."
          etiquetaDeEliminar="Eliminar grupo"
          dnd={e}
          onCrear={() => acciones.nuevoItem(nodo.grupo, null)}
          onEliminar={() => acciones.eliminarVacio(nodo.grupo, null)}
        />,
      );
      return;
    }

    nodo.subgrupos.forEach((sub) => {
      if (sub.filas.length === 0 && !sub.vacio) return;

      const directo = sub.subgrupo === null;
      const sk = claveDeSubgrupo(nodo.grupo, sub.subgrupo);
      const plegado = !filtrando && !directo && subgrupoColapsado(nodo.grupo, sub.subgrupo);
      const mostrarEncabezado = !directo || nodo.subgrupos.length > 1 || sub.vacio;

      if (mostrarEncabezado) {
        filas.push(
          <EncabezadoDeSubgrupo
            key={sk}
            nodo={nodo}
            sub={sub}
            plegado={plegado}
            filtrando={filtrando}
            esNuevo={
              !directo &&
              grupoEnElMaestro(nodo.grupo) &&
              !subgrupoEnElMaestro(nodo.grupo, sub.subgrupo as string)
            }
            dnd={dnd(
              sk,
              "S",
              { tipo: "subgrupo", grupo: nodo.grupo, subgrupo: sub.subgrupo },
              directo ? undefined : { tipo: "subgrupo", grupo: nodo.grupo, subgrupo: sub.subgrupo },
            )}
            acciones={acciones}
          />,
        );
      }

      if (plegado) return;

      if (sub.filas.length === 0) {
        const ek = `E:${nodo.grupo}|${sub.subgrupo ?? ""}`;
        filas.push(
          <FilaVacia
            key={ek}
            sangria="ml-6"
            mensaje="Subgrupo vacío. Arrastra ítems aquí o crea uno."
            etiquetaDeEliminar="Eliminar subgrupo"
            dnd={dnd(ek, "E", {
              tipo: "subgrupo",
              grupo: nodo.grupo,
              subgrupo: sub.subgrupo,
            })}
            onCrear={() => acciones.nuevoItem(nodo.grupo, sub.subgrupo)}
            onEliminar={() => acciones.eliminarVacio(nodo.grupo, sub.subgrupo)}
          />,
        );
        return;
      }

      const sangria = mostrarEncabezado ? "ml-6" : "ml-2";
      sub.filas.forEach(({ fila }) => {
        const ref = fila.item as string;
        const item = items[ref];
        if (!item) return;
        const ik = `I:${ref}`;
        const i = dnd(ik, "I", { tipo: "item", item: ref }, { tipo: "item", item: ref });

        filas.push(
          <div
            key={ik}
            data-testid={`item-${ref}`}
            draggable={i.draggable}
            onDragStart={i.onDragStart}
            onDragEnd={i.onDragEnd}
            onDragOver={i.onDragOver}
            onDrop={i.onDrop}
            onClick={() => acciones.seleccionar(ref)}
            className={cn(
              "hover:bg-superficie-sutil flex cursor-pointer items-center gap-2.5 rounded-[0.625rem] py-2 pr-2.5 pl-3",
              sangria,
              seleccionado === ref && "bg-success-soft/50 hover:bg-success-soft/50",
              i.className,
            )}
          >
            <GripVertical
              aria-hidden="true"
              className={cn("text-ink-ghost size-3.5 shrink-0", !filtrando && "cursor-grab")}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    acciones.seleccionar(ref);
                  }}
                  className="text-ink min-w-0 truncate text-left text-[0.84375rem] font-semibold outline-none focus-visible:underline"
                >
                  {item.nombre}
                </button>
                {!item.local ? (
                  <InsigniaDeIa
                    modo={item.modo}
                    tipo={item.tipo}
                    nombre={item.nombre}
                    dependencias={item.dependencias.length}
                    alFinal={item.seRedactaAlFinal}
                  />
                ) : null}
                {item.local ? (
                  <Badge variant="info">
                    {item.publicar ? "Pasará al maestro al guardar" : "Solo en esta plantilla"}
                  </Badge>
                ) : null}
              </div>
              {fila.obligatoriedad === "condicional" && (fila.condicion || item.condicionSugerida) ? (
                <p className="text-warning-soft-foreground truncate text-[0.71875rem] font-semibold">
                  {fila.condicion || item.condicionSugerida}
                </p>
              ) : null}
            </div>
            <ChipDeTipo tipo={item.tipo} ancho className="hidden lg:inline-flex" />
            <span className="text-ink-hint hidden w-[9.375rem] shrink-0 truncate text-xs font-medium xl:block">
              {item.fuente || "—"}
            </span>
            {/* El clic de la píldora cambia el valor y NO abre el cajón. */}
            <span onClick={(e) => e.stopPropagation()}>
              <PildoraDeObligatoriedad
                obligatoriedad={fila.obligatoriedad ?? "opcional"}
                titulo="Clic para cambiar: obligatorio, condicional u opcional"
                onClick={() => acciones.ciclar(ref)}
                className="w-[6.25rem] px-2 py-1"
              />
            </span>
            <span className="flex items-center gap-0.5 opacity-45 transition-opacity hover:opacity-100 focus-within:opacity-100">
              <BotonDeFila
                etiqueta={`Subir ${item.nombre}`}
                onClick={() => acciones.moverItem(ref, -1)}
              >
                <ChevronUp />
              </BotonDeFila>
              <BotonDeFila
                etiqueta={`Bajar ${item.nombre}`}
                onClick={() => acciones.moverItem(ref, 1)}
              >
                <ChevronDown />
              </BotonDeFila>
              <BotonDeFila
                etiqueta={`Mover ${item.nombre} a otro grupo o subgrupo`}
                onClick={() => acciones.pedirMoverItem(ref)}
              >
                <Move />
              </BotonDeFila>
              <BotonDeFila
                etiqueta={`Quitar ${item.nombre}`}
                destructivo
                onClick={() => acciones.pedirQuitar(ref)}
              >
                <X />
              </BotonDeFila>
            </span>
          </div>,
        );
      });
    });
  });

  return <div className="bg-card rounded-xl border p-2">{filas}</div>;
}

function EncabezadoDeSubgrupo({
  nodo,
  sub,
  plegado,
  filtrando,
  esNuevo,
  dnd,
  acciones,
}: {
  nodo: NodoDeGrupo;
  sub: NodoDeSubgrupo;
  plegado: boolean;
  filtrando: boolean;
  esNuevo: boolean;
  dnd: PropsDeArrastre;
  acciones: AccionesDelArbol;
}) {
  const d = dnd;
  const directo = sub.subgrupo === null;
  const titulo = directo ? "Sin subgrupo" : (sub.subgrupo as string);

  return (
    <div
      data-testid={`subgrupo-${nodo.grupo}-${sub.subgrupo ?? ""}`}
      draggable={d.draggable}
      onDragStart={d.onDragStart}
      onDragEnd={d.onDragEnd}
      onDragOver={d.onDragOver}
      onDrop={d.onDrop}
      className={cn(
        "mt-2.5 mb-1 ml-6 flex items-center gap-2 rounded-[0.625rem] py-[0.4375rem] pr-2 pl-2.5",
        !directo && "bg-muted/60",
        d.className,
      )}
    >
      {directo ? (
        <span className="size-3 shrink-0" />
      ) : (
        <button
          type="button"
          aria-expanded={!plegado}
          aria-label={`${plegado ? "Expandir" : "Colapsar"} subgrupo ${titulo}`}
          onClick={() => acciones.alternarSubgrupo(nodo.grupo, sub.subgrupo)}
          className="text-ink-hint flex size-4 shrink-0 items-center justify-center rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <ChevronRight className={cn("size-3 transition-transform", !plegado && "rotate-90")} />
        </button>
      )}
      <span
        className={cn(
          "text-[0.84375rem] font-bold tracking-[-0.005em]",
          directo ? "text-ink-muted" : "text-ink",
        )}
      >
        {titulo}
      </span>
      <span className="text-ink-hint font-mono text-[0.6875rem] font-semibold">
        {sub.vacio ? 0 : filtrando ? sub.filas.length : sub.total}
      </span>
      {esNuevo ? <Badge variant="info">Nuevo subgrupo</Badge> : null}
      {!directo ? (
        <span className="ml-auto flex items-center gap-1">
          <ChipDeAgregar blanco onClick={() => acciones.nuevoItem(nodo.grupo, sub.subgrupo)}>
            Ítem
            <span className="sr-only"> en {titulo}</span>
          </ChipDeAgregar>
          <BotonDeFila
            etiqueta={`Subir el subgrupo ${titulo}`}
            onClick={() => acciones.moverSubgrupo(nodo.grupo, sub.subgrupo, -1)}
          >
            <ChevronUp />
          </BotonDeFila>
          <BotonDeFila
            etiqueta={`Bajar el subgrupo ${titulo}`}
            onClick={() => acciones.moverSubgrupo(nodo.grupo, sub.subgrupo, 1)}
          >
            <ChevronDown />
          </BotonDeFila>
          <BotonDeFila
            etiqueta={`Mover el subgrupo ${titulo} a otro grupo`}
            onClick={() => acciones.pedirMoverSubgrupo(nodo.grupo, sub.subgrupo)}
          >
            <Move />
          </BotonDeFila>
        </span>
      ) : null}
    </div>
  );
}

function FilaVacia({
  sangria,
  mensaje,
  etiquetaDeEliminar,
  dnd,
  onCrear,
  onEliminar,
}: {
  sangria: string;
  mensaje: string;
  etiquetaDeEliminar: string;
  dnd: PropsDeArrastre;
  onCrear: () => void;
  onEliminar: () => void;
}) {
  return (
    <div
      onDragOver={dnd.onDragOver}
      onDrop={dnd.onDrop}
      className={cn(
        "border-input mt-1 mr-2.5 mb-1.5 flex flex-wrap items-center gap-2.5 rounded-md border-[1.5px] border-dashed px-3.5 py-3",
        sangria,
        dnd.className,
      )}
    >
      <span className="text-ink-hint min-w-[11rem] flex-1 text-[0.78rem] font-semibold">
        {mensaje}
      </span>
      <Button type="button" variant="secondary" size="sm" onClick={onCrear}>
        <Plus className="size-3.5" />
        Crear ítem
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={onEliminar}
      >
        {etiquetaDeEliminar}
      </Button>
    </div>
  );
}
